import React, {
	memo,
	Suspense,
	startTransition,
	useEffect,
	useState,
	useSyncExternalStore,
} from 'react';

import isEmpty from 'lodash/isEmpty';
import { graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';

import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import type { GlobalShortcutsItem, ShortcutsConfig } from '../types';
import globalShortcutsRelayClientQueryNode, {
	type GlobalShortcutsRelayClientQuery,
} from './__generated__/GlobalShortcutsRelayClientQuery.graphql';
import type {
	InferShortcutsOutput,
	UseGlobalShortcutDataInputBase,
} from './use-global-shortcuts-data/types';
import { useGlobalShortcutsDataRelay } from './use-global-shortcuts-data/useGlobalShortcutsDataRelay';

export type GlobalShortcutsRelayClientProps<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
> = UseGlobalShortcutDataInputBase & {
	children: (shortcutItems: InferShortcutsOutput<T>) => React.ReactNode;
	shortcutsConfig: T;
};

// ---------------------------------------------------------------------------
// HydrationSafeSuspense — inline equivalent of @confluence/hydration-utils
//
// The inner Suspense boundary must not receive prop updates until after the
// first mount (useEffect), otherwise React throws error #421 ("Suspense
// boundary received an update before it finished hydrating").
//
// This covers two scenarios:
//   1. SSR hydration (Bifrost view-page): React reconciles server HTML on client.
//   2. Client-side transitions (e.g. edit mode): React renders fresh on client;
//      no SSR, but useEffect-triggered state updates (loadQuery) still race
//      against the initial render completion.
//
// Strategy: `isLocked` always starts `true` (regardless of SSR or CSR) and
// flips to `false` via useEffect after the first mount. The React.memo custom
// comparator returns `true` while locked, blocking all prop propagation into
// the inner Suspense boundary until after mount.
// ---------------------------------------------------------------------------

interface HydrationSafeSuspenseContentProps {
	children: React.ReactNode;
	fallback: React.ReactNode;
	isLocked: boolean;
}

const HydrationSafeSuspenseContent = memo(
	function HydrationSafeSuspenseContent({
		children,
		fallback,
	}: HydrationSafeSuspenseContentProps): React.JSX.Element {
		return <Suspense fallback={fallback}>{children}</Suspense>;
	},
	(_prevProps: HydrationSafeSuspenseContentProps, nextProps: HydrationSafeSuspenseContentProps) => {
		// Block prop propagation into the Suspense boundary while locked (during hydration)
		return nextProps.isLocked;
	},
);

interface HydrationSafeSuspenseProps {
	children: React.ReactNode;
	fallback?: React.ReactNode;
}

function HydrationSafeSuspense({
	fallback = null,
	children,
}: HydrationSafeSuspenseProps): React.JSX.Element {
	// Always start locked (true) regardless of SSR or CSR — this covers both SSR hydration
	// AND client-side transitions (e.g. edit mode) where process.env.REACT_SSR is false
	// but React still needs the boundary to stay stable until after the first mount.
	const [isLocked, setIsLocked] = useState(true);

	useEffect(() => {
		setIsLocked(false);
	}, []);

	return (
		<HydrationSafeSuspenseContent isLocked={isLocked} fallback={fallback}>
			{children}
		</HydrationSafeSuspenseContent>
	);
}

const emptySubscribe = () => () => {};
/**
 * Returns `false` during SSR and the client's hydrating render, then `true` once React has finished
 * hydrating this subtree
 */
function useIsHydrated(): boolean {
	return useSyncExternalStore(
		emptySubscribe,
		() => true,
		() => false,
	);
}

/**
 * Standalone GAS Relay entry: includes `ConditionalRelayEnvironmentProvider` and
 * `HydrationSafeSuspense` so callers do not need to wrap the tree.
 *
 * Guards against React error #421 ("Suspense boundary received an update before it finished
 * hydrating"). When the `navx-4463-gas-agg-migration` gate is on, `GlobalShortcutsRelayClientImpl`
 * calls `loadQuery()` inside a `useEffect` on mount, which updates Relay's query-loader state while
 * the surrounding boundary is still hydrating.
 *
 * We render the non-suspending `GlobalShortcutsRelayClientSkip` until after the first mount
 * (`isHydrated`), so the data-fetching `HydrationSafeSuspense` boundary only mounts post-hydration
 * and can never trip #421.
 *
 * Fallback uses `GlobalShortcutsRelayClientSkip` (no preload, same merge path).
 */
export default function GlobalShortcutsRelayClient<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>(props: GlobalShortcutsRelayClientProps<T>): React.JSX.Element {
	const isHydrated = useIsHydrated();

	// Render Skip until after mount so the Suspense boundary only mounts post-hydration (avoids #421).
	// `ConditionalRelayEnvironmentProvider` keeps whichever environment the consumer already provides
	// and only supplies the internal one when there is none. The provider wraps `children`, so
	// shadowing a consumer-provided environment would detach the whole subtree (the side navigation,
	// in the case of `@atlassian/global-side-navigation`) from the environment its data was loaded
	// into.
	return (
		<ConditionalRelayEnvironmentProvider>
			{!isHydrated ? (
				<GlobalShortcutsRelayClientSkip {...props} />
			) : (
				<HydrationSafeSuspense fallback={<GlobalShortcutsRelayClientSkip {...props} />}>
					<GlobalShortcutsRelayClientImpl {...props} />
				</HydrationSafeSuspense>
			)}
		</ConditionalRelayEnvironmentProvider>
	);
}

function GlobalShortcutsRelayClientImpl<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>(props: GlobalShortcutsRelayClientProps<T>): React.JSX.Element {
	const shouldFetch =
		Boolean(props.cloudId) && !props.isShortcutsStatic && !isEmpty(props.shortcutsConfig);

	const [queryRef, loadQuery] = useQueryLoader<GlobalShortcutsRelayClientQuery>(
		globalShortcutsRelayClientQueryNode,
	);

	useEffect(() => {
		if (!shouldFetch) {
			return;
		}
		startTransition(() => {
			loadQuery({ cloudId: props.cloudId ?? '' });
		});
	}, [shouldFetch, props.cloudId, loadQuery]);

	if (!shouldFetch || queryRef == null) {
		return <GlobalShortcutsRelayClientSkip {...props} />;
	}

	return <GlobalShortcutsRelayClientPreloaded queryRef={queryRef} {...props} />;
}

function GlobalShortcutsRelayClientSkip<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>({
	children,
	shortcutsConfig,
	cloudId,
	onError,
	product,
	onUpdateGetFilteredProductLinks,
}: GlobalShortcutsRelayClientProps<T>): React.JSX.Element {
	const shortcutItems = useGlobalShortcutsDataRelay({
		queryRef: undefined,
		shortcutsConfig,
		cloudId,
		onError,
		product,
		onUpdateGetFilteredProductLinks,
	});

	return <>{children(shortcutItems as InferShortcutsOutput<T>)}</>;
}

function GlobalShortcutsRelayClientPreloaded<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>({
	queryRef,
	children,
	shortcutsConfig,
	cloudId,
	onError,
	product,
	onUpdateGetFilteredProductLinks,
}: GlobalShortcutsRelayClientProps<T> & {
	queryRef: PreloadedQuery<GlobalShortcutsRelayClientQuery>;
}): React.JSX.Element {
	const queryData = usePreloadedQuery<GlobalShortcutsRelayClientQuery>(
		graphql`
			query GlobalShortcutsRelayClientQuery($cloudId: ID!) {
				...useGlobalShortcutsDataRelay
			}
		`,
		queryRef,
	);

	const shortcutItems = useGlobalShortcutsDataRelay({
		queryRef: queryData,
		shortcutsConfig,
		cloudId,
		onError,
		product,
		onUpdateGetFilteredProductLinks,
	});

	return <>{children(shortcutItems as InferShortcutsOutput<T>)}</>;
}
